import { Card, Col, Row, Statistic, message, List, Tag, Progress, Divider, Button, DatePicker } from "antd";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../utils/axios";
import { FormatVND } from "../../utils/format";
import {
    ShoppingCartOutlined,
    UserOutlined,
    BookOutlined,
    WalletOutlined,
    RocketOutlined,
    FileTextOutlined,
    ArrowRightOutlined,
    SyncOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
const { RangePicker } = DatePicker;

export default function Dashboard() {
    const [stats, setStats] = useState({
        totalOrders: 0,
        totalRevenue: 0,
        totalUsers: 0,
        totalProducts: 0,
        totalBlogs: 0,
        orderStatus: [],
        recentOrders: []
    });
    const [loading, setLoading] = useState(false);

    const [date, setDate] = useState({
        fromDate: dayjs().startOf('month').format('YYYY-MM-DD'),
        toDate: dayjs().endOf('month').format('YYYY-MM-DD')
    });

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            // Parallel data fetching
            const [orderRes, userRes, productRes, blogRes] = await Promise.all([
                axiosInstance.get(`/order/getAll`, { params: { page: 1, pageSize: 5, fromDate: date.fromDate, toDate: date.toDate } }),
                axiosInstance.get(`/user/getAll`, { params: { page: 1, pageSize: 1 } }),
                axiosInstance.get(`/product/getAll`, { params: { page: 1, pageSize: 1 } }),
                axiosInstance.get(`/blog/getAll`, { params: { page: 1, pageSize: 1 } })
            ]);

            const statusData = orderRes?.data?.totalStatus || [];
            const totalOrdersCalc = statusData.reduce((acc, curr) => acc + (parseInt(curr.total) || 0), 0);
            
            // Accurate revenue summary from backend (sum of successful orders)
            const successfulStatus = statusData.find(s => String(s.status) === "1");
            const totalRevenue = parseInt(successfulStatus?.total_price) || 0;

            const realStats = {
                totalOrders: orderRes?.data?.total || totalOrdersCalc || 0,
                totalRevenue: totalRevenue,
                totalUsers: userRes?.data?.total?.[0]?.total || 0,
                totalProducts: productRes?.data?.total || 0,
                totalBlogs: blogRes?.data?.total || 0,
                orderStatus: statusData,
                recentOrders: orderRes?.data?.data || []
            };

            // Relaxed Mock Data Fallback: trigger if there's no revenue AND no orders
            if (realStats.totalOrders === 0) {
                setStats({
                    totalOrders: 158,
                    totalRevenue: 42500000,
                    totalUsers: 842,
                    totalProducts: 45,
                    totalBlogs: 12,
                    orderStatus: [
                        { id: 0, total: 15 },
                        { id: 1, total: 112 },
                        { id: 2, total: 24 },
                        { id: 3, total: 7 }
                    ],
                    recentOrders: [
                        { order_id: 1024, user_name: "Nguyễn Văn A", total_price: 1200000, status: 1, create_at: new Date().toISOString() },
                        { order_id: 1025, user_name: "Trần Thị B", total_price: 850000, status: 2, create_at: new Date().toISOString() },
                        { order_id: 1026, user_name: "Lê Văn C", total_price: 2100000, status: 0, create_at: new Date().toISOString() },
                        { order_id: 1027, user_name: "Phạm Minh D", total_price: 450000, status: 1, create_at: new Date().toISOString() },
                        { order_id: 1028, user_name: "Hoàng Anh E", total_price: 1500000, status: 3, create_at: new Date().toISOString() },
                    ],
                    isDemo: true
                });
            } else {
                setStats({ ...realStats, isDemo: false });
            }
        } catch (error) {
            console.error("Dashboard error:", error);
            message.error("Lỗi khi tải dữ liệu thống kê!");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    const orderStatusConfig = [
        { id: 0, color: '#faad14', label: 'Chưa xác nhận' },
        { id: 1, color: '#52c41a', label: 'Thành công' },
        { id: 2, color: '#1890ff', label: 'Đang giao' },
        { id: 3, color: '#ff4d4f', label: 'Đã hủy' },
    ];

    // Calculate percentages for progress bars using a more robust mapping
    const enrichedStatus = orderStatusConfig.map(config => {
        // Try to find status by ID if available, otherwise fallback to matching data string or index
        const item = stats.orderStatus.find(s => s.id === config.id) || stats.orderStatus[config.id];
        const count = parseInt(item?.total) || 0;
        const percent = stats.totalOrders > 0 ? Math.round((count / stats.totalOrders) * 100) : 0;
        return { ...config, count, percent };
    });

    return (
        <div className="p-3 sm:p-4 lg:p-6 bg-[#f0f2f5] min-h-full">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-5 lg:mb-8">
                <div>
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-[#1a3353] m-0 flex items-center gap-2 lg:gap-3">
                        <RocketOutlined className="text-blue-600" />
                        Bảng tin hệ thống {stats.isDemo && <Tag color="orange" className="ml-2 font-bold uppercase">Demo Data</Tag>}
                    </h1>
                    <p className="text-gray-500 mt-1">
                        {stats.isDemo
                            ? "Hệ thống đang hiển thị dữ liệu mẫu để minh họa. Hãy thêm đơn hàng thật để cập nhật."
                            : "Chào mừng quay lại! Đây là tổng quan về hoạt động của bạn hôm nay."
                        }
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 lg:gap-4 w-full lg:w-auto">
                    <RangePicker
                        className="w-full sm:w-auto"
                        defaultValue={[dayjs(date.fromDate), dayjs(date.toDate)]}
                        onChange={(range, dateStrings) => {
                            if (range) {
                                setDate({
                                    fromDate: dateStrings[0],
                                    toDate: dateStrings[1]
                                });
                            }
                        }}
                    />
                    <Button
                        icon={<SyncOutlined spin={loading} />}
                        onClick={fetchDashboardData}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl lg:rounded-full px-5 h-10 shadow-sm border-none bg-white text-blue-600 font-semibold hover:text-blue-700 hover:shadow-md transition-all"
                    >
                        Làm mới
                    </Button>
                </div>
            </div>

            {/* Top Metrics Cards */}
            <Row gutter={[12, 12]} className="lg:!mx-[-12px]">
                <Col xs={24} sm={12} xl={8}>
                    <Card bordered={false} className="rounded-2xl shadow-sm hover:shadow-md transition-all border-l-4 border-[#ef4444]">
                        <Statistic
                            title={<span className="text-gray-400 font-medium text-lg">Tổng doanh thu</span>}
                            value={stats.totalRevenue}
                            formatter={(value) => <span className="text-2xl font-bold text-[#ef4444]">{FormatVND(value)}</span>}
                            prefix={<WalletOutlined className="p-3 bg-red-50 text-red-500 rounded-xl mr-3 text-xl" />}
                            loading={loading}
                        />
                        <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-400 flex items-center justify-between">
                            <span>Tháng này</span>
                            <span className="text-green-500 font-bold flex items-center gap-1">
                                <SyncOutlined /> Cập nhật mới nhất
                            </span>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} xl={8}>
                    <Card bordered={false} className="rounded-2xl shadow-sm hover:shadow-md transition-all border-l-4 border-[#3b82f6]">
                        <Statistic
                            title={<span className="text-gray-400 font-medium text-lg">Đơn hàng</span>}
                            value={stats.totalOrders}
                            valueStyle={{ fontWeight: 'bold', fontSize: '1.5rem' }}
                            prefix={<ShoppingCartOutlined className="p-3 bg-blue-50 text-blue-600 rounded-xl mr-3 text-xl" />}
                            loading={loading}
                        />
                        <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                            <span className="text-xs text-gray-400">Xem tất cả đơn hàng</span>
                            <Link to="/admin/quan-ly-don-hang">
                                <ArrowRightOutlined className="text-blue-500" />
                            </Link>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} xl={8}>
                    <Card bordered={false} className="rounded-2xl shadow-sm hover:shadow-md transition-all border-l-4 border-[#10b981]">
                        <Statistic
                            title={<span className="text-gray-400 font-medium text-lg">Khách hàng</span>}
                            value={stats.totalUsers}
                            valueStyle={{ fontWeight: 'bold', fontSize: '1.5rem' }}
                            prefix={<UserOutlined className="p-3 bg-green-50 text-green-600 rounded-xl mr-3 text-xl" />}
                            loading={loading}
                        />
                        <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                            <span className="text-xs text-gray-400">Xem danh sách user</span>
                            <Link to="/admin/quan-ly-nguoi-dung">
                                <ArrowRightOutlined className="text-green-500" />
                            </Link>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} xl={12}>
                    <Card bordered={false} className="rounded-2xl shadow-sm hover:shadow-md transition-all border-l-4 border-[#8b5cf6]">
                        <Statistic
                            title={<span className="text-gray-400 font-medium">Sản phẩm</span>}
                            value={stats.totalProducts}
                            prefix={<BookOutlined className="p-2 bg-purple-50 text-purple-600 rounded-lg mr-2" />}
                            loading={loading}
                        />
                        <div className="mt-2 text-xs text-purple-500 font-semibold flex items-center gap-1">
                            <ArrowRightOutlined /> Quản lý sản phẩm
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} xl={12}>
                    <Card bordered={false} className="rounded-2xl shadow-sm hover:shadow-md transition-all border-l-4 border-[#f59e0b]">
                        <Statistic
                            title={<span className="text-gray-400 font-medium">Bài viết Blog</span>}
                            value={stats.totalBlogs}
                            prefix={<FileTextOutlined className="p-2 bg-orange-50 text-orange-600 rounded-lg mr-2" />}
                            loading={loading}
                        />
                        <div className="mt-2 text-xs text-orange-500 font-semibold flex items-center gap-1">
                            <ArrowRightOutlined /> Viết bài mới
                        </div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[12, 12]} className="mt-4 lg:mt-8 lg:!mx-[-12px]">
                {/* Order Status Visualization */}
                <Col xs={24} lg={10}>
                    <Card
                        title={<span className="font-bold text-[#1a3353]">Phân bổ trạng thái đơn</span>}
                        className="rounded-2xl shadow-sm h-full"
                        bordered={false}
                    >
                        <div className="space-y-6">
                            {enrichedStatus.map(item => (
                                <div key={item.id}>
                                    <div className="flex justify-between mb-2">
                                        <span className="text-gray-600 font-medium">{item.label}</span>
                                        <span className="font-bold text-[#1a3353]">{item.count} đơn</span>
                                    </div>
                                    <Progress
                                        percent={item.percent}
                                        strokeColor={item.color}
                                        trailColor="#f1f5f9"
                                        strokeWidth={10}
                                        showInfo={true}
                                        format={(percent) => <span className="text-gray-400 font-semibold">{percent}%</span>}
                                    />
                                </div>
                            ))}
                        </div>
                        <Divider className="my-6" />
                        <div className="p-4 bg-gray-50 rounded-xl">
                            <p className="text-gray-500 text-sm mb-0 italic">Mẹo: Bạn có thể cập nhật trạng thái đơn hàng trực tiếp trong phần Quản lý đơn hàng để thay đổi các số liệu này.</p>
                        </div>
                    </Card>
                </Col>

                {/* Recent Activity / Action Panel */}
                <Col xs={24} lg={14}>
                    <div className="flex flex-col gap-6 h-full">
                        {/* Recent Orders List */}
                        <Card
                            title={<span className="font-bold text-[#1a3353]">Đơn hàng gần đây</span>}
                            extra={<Link to="/admin/quan-ly-don-hang" className="text-blue-600 font-semibold">Xem tất cả</Link>}
                            className="rounded-2xl shadow-sm border-none"
                            bodyStyle={{ padding: '4px 24px' }}
                        >
                            <List
                                loading={loading}
                                dataSource={stats.recentOrders}
                                renderItem={(item) => (
                                    <List.Item className="hover:bg-gray-50 px-2 rounded-lg transition-all border-b border-gray-100 last:border-none">
                                        <List.Item.Meta
                                            avatar={
                                                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold">
                                                    #{item.order_id}
                                                </div>
                                            }
                                            title={<span className="font-semibold text-[#1a3353]">{item.user_name || "Khách ẩn danh"}</span>}
                                            description={<span className="text-xs text-gray-400">{dayjs(item.create_at).format("DD/MM/YYYY HH:mm")}</span>}
                                        />
                                        <div className="text-right flex flex-col items-end gap-1">
                                            <span className="font-bold text-[#1a3353]">{FormatVND(item.total_price)}</span>
                                            <Tag color={orderStatusConfig[item.status]?.color || 'default'} className="m-0 border-none rounded-full px-3 text-[10px] uppercase font-bold">
                                                {orderStatusConfig[item.status]?.label || 'Không xác định'}
                                            </Tag>
                                        </div>
                                    </List.Item>
                                )}
                                locale={{ emptyText: <div className="py-10 text-gray-400">Chưa có đơn hàng nào</div> }}
                            />
                        </Card>

                        {/* Quick Access Grid */}
                        <Card
                            title={<span className="font-bold text-[#1a3353]">Lối tắt quản lý</span>}
                            className="rounded-2xl shadow-sm border-none"
                        >
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4 text-center">
                                <Link to="/admin/quan-ly-don-hang" className="group">
                                    <div className="p-4 bg-blue-50 text-blue-700 rounded-2xl group-hover:bg-blue-100 transition shadow-sm mb-2">
                                        <ShoppingCartOutlined className="text-2xl" />
                                    </div>
                                    <span className="text-xs font-bold text-gray-600">Đơn hàng</span>
                                </Link>
                                <Link to="/admin/quan-ly-san-pham" className="group">
                                    <div className="p-4 bg-green-50 text-green-700 rounded-2xl group-hover:bg-green-100 transition shadow-sm mb-2">
                                        <BookOutlined className="text-2xl" />
                                    </div>
                                    <span className="text-xs font-bold text-gray-600">Sản phẩm</span>
                                </Link>
                                <Link to="/admin/quan-ly-blog" className="group">
                                    <div className="p-4 bg-purple-50 text-purple-700 rounded-2xl group-hover:bg-purple-100 transition shadow-sm mb-2">
                                        <FileTextOutlined className="text-2xl" />
                                    </div>
                                    <span className="text-xs font-bold text-gray-600">Bài viết</span>
                                </Link>
                                <Link to="/admin/cau-hinh-iframe" className="group">
                                    <div className="p-4 bg-orange-50 text-orange-700 rounded-2xl group-hover:bg-orange-100 transition shadow-sm mb-2">
                                        <WalletOutlined className="text-2xl" />
                                    </div>
                                    <span className="text-xs font-bold text-gray-600">Iframe</span>
                                </Link>
                            </div>
                        </Card>
                    </div>
                </Col>
            </Row>
        </div>
    );
}
